'use client';

import { useEffect, useMemo, useState } from 'react';
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Feature, FeatureCollection, Geometry } from 'geojson';

/** us-atlas FIPS state id → postal code (matches our `state` filter values). */
const FIPS_TO_CODE: Readonly<Record<string, string>> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO', '09': 'CT',
  '10': 'DE', '11': 'DC', '12': 'FL', '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL',
  '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME', '24': 'MD',
  '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE',
  '32': 'NV', '33': 'NH', '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD',
  '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV',
  '55': 'WI', '56': 'WY',
};

interface StateShape {
  code: string;
  name: string;
  d: string;
  centroid: [number, number];
}

/**
 * Clickable US choropleth. States we serve are filled in brand blue and drive
 * the same `state` filter as the dropdown; the rest are inert. The us-atlas
 * topojson is pre-projected (975×610), so paths render with an identity
 * geoPath and no runtime projection. Loaded from /public so it stays out of
 * the JS bundle.
 */
export function LocationsMap({
  servedCounts,
  selected,
  onSelect,
}: {
  servedCounts: ReadonlyMap<string, number>;
  selected: string;
  onSelect: (code: string) => void;
}) {
  const [shapes, setShapes] = useState<StateShape[]>([]);
  const [hover, setHover] = useState<{ name: string; count: number; x: number; y: number } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/data/us-states-10m.json', { signal: controller.signal })
      .then((response) => response.json())
      .then((topo) => {
        const collection = feature(topo, topo.objects.states) as unknown as FeatureCollection<
          Geometry,
          { name: string }
        >;
        // us-atlas is geographic (lon/lat); project to the 975×610 canvas.
        const projection = geoAlbersUsa().fitSize([975, 610], collection);
        const path = geoPath(projection);
        const features = collection.features as Feature<Geometry, { name: string }>[];
        const next: StateShape[] = [];
        for (const f of features) {
          const code = FIPS_TO_CODE[String(f.id)];
          const d = path(f);
          const centroid = path.centroid(f);
          if (code && d) next.push({ code, name: f.properties.name, d, centroid });
        }
        setShapes(next);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  const served = useMemo(() => servedCounts, [servedCounts]);

  return (
    <div className="relative">
      <svg viewBox="0 0 975 610" className="h-auto w-full" role="img" aria-label="Map of the states we serve">
        {shapes.map((shape) => {
          const isServed = served.has(shape.code);
          const isSelected = selected === shape.code;
          const fill = isSelected
            ? 'var(--color-brand-yellow)'
            : isServed
              ? 'var(--color-brand-royal)'
              : 'var(--color-brand-navy)';
          const opacity = isSelected ? 1 : isServed ? 0.85 : 0.08;
          return (
            <path
              key={shape.code}
              d={shape.d}
              fill={fill}
              fillOpacity={opacity}
              stroke="white"
              strokeWidth={0.75}
              className={isServed ? 'cursor-pointer transition-[fill-opacity] hover:fill-opacity-100' : ''}
              onClick={isServed ? () => onSelect(isSelected ? 'all' : shape.code) : undefined}
              onMouseEnter={
                isServed
                  ? () =>
                      setHover({
                        name: shape.name,
                        count: served.get(shape.code) ?? 0,
                        x: shape.centroid[0],
                        y: shape.centroid[1],
                      })
                  : undefined
              }
              onMouseLeave={() => setHover(null)}
            >
              <title>{isServed ? `${shape.name} — ${served.get(shape.code)} cities` : shape.name}</title>
            </path>
          );
        })}
      </svg>
      {hover ? (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg bg-brand-navy px-3 py-1.5 text-xs font-semibold whitespace-nowrap text-white shadow-lg"
          style={{ left: `${(hover.x / 975) * 100}%`, top: `${(hover.y / 610) * 100}%` }}
        >
          {hover.name}: {hover.count} {hover.count === 1 ? 'city' : 'cities'}
        </div>
      ) : null}
    </div>
  );
}
